import { makeAutoObservable, runInAction } from 'mobx'
import { type RequestCancelAxios, RequestLoadData } from 'shared/api/lib'
import {
  type IQuickLink,
  type IResponseQuickLinksList,
  QuickLink,
  QuickLinkApi,
} from 'entities/QuickLink'

export type IQuickLinksSearchStore = QuickLinksSearchStore

export class QuickLinksSearchStore {
  private _term = ''

  private _initialLoading = true
  private _loadingScheduled = false

  private _quickLinks = new Map<number, IQuickLink>()

  constructor(private _contactId: number) {
    makeAutoObservable(this)
  }

  get term() {
    return this._term
  }

  get loading() {
    return this._requestTryMake.loading
  }

  get initialLoading() {
    return this._initialLoading
  }

  get loadingScheduled() {
    return this._loadingScheduled
  }

  get quickLinks() {
    return Array.from(this._quickLinks.values())
  }

  get isEmpty() {
    return !this._quickLinks.size
  }

  get noTerm() {
    return !this._term.length
  }

  search = async (term: string, immediate = false) => {
    this._loadingScheduled = true
    this._term = term

    const makeSearch = async (cancelRequest: RequestCancelAxios) => {
      runInAction(() => this._quickLinks.clear())

      const { data } = await QuickLinkApi.getContactLinks(
        this._contactId,
        { search: this._term, length: 100 },
        { cancelToken: cancelRequest.token }
      )

      runInAction(() => this._setQuickLinks(data))
    }

    immediate ? this._tryMakeRequest(makeSearch) : this._tryMakeRequestDelay(makeSearch)
  }

  dispose = () => {
    this._requestTryMake.dispose()
  }

  private _setQuickLinks = (links: IResponseQuickLinksList) => {
    this._quickLinks.clear()
    links.forEach((item) => {
      const quickLink = new QuickLink(item)

      this._quickLinks.set(quickLink.id, quickLink)
    })
  }

  private _requestTryMake = new RequestLoadData<
    (cancelRequest: RequestCancelAxios) => Promise<void>
  >({
    requestCallback: async ({ cancelRequest }, action) => {
      await action?.(cancelRequest)

      runInAction(() => {
        this._initialLoading = false
        this._loadingScheduled = false
      })
    },
  })

  private _tryMakeRequest = async (
    action: (cancelRequest: RequestCancelAxios) => Promise<void>
  ) => {
    return this._requestTryMake.loadData(action)
  }

  private _tryMakeRequestDelay = this._requestTryMake.debounceLoadData
}
